import { useMemo } from 'react'
import {Navigate, useParams} from 'react-router-dom'
import { useWorkspace } from '../../../contexts/WorkspaceContext'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import {
    toggleCompleteSaga,
    updateTaskSaga
} from '../../../store/tasks/tasksSlice'
import { selectAllTasks, selectTasksLoading } from '../../../store/tasks/tasksSelectors'
import { selectAllProjects, selectProjectsLoading, selectMemberProjectIds } from '../../../store/projects/projectsSelectors'
import { selectAllLists, selectListsLoading } from '../../../store/lists/listsSelectors'
import { TextH2 } from '../../ui/Text'
import Section from '../../app/Section/Section'
import Link from '../../app/Link/Link'
import Empty from '../../ui/Empty/Empty'
import PlateLoader from '../../ui/Loader/PlateLoader'
import css from './WorkspaceAllTasksPage.module.css'

const WorkspaceAllTasksPage = () => {
    const dispatch = useAppDispatch()
    const { workspace: workspaceUrlId } = useParams<{ workspace: string }>()
    const { workspace, loading } = useWorkspace()

    const tasks = useAppSelector(selectAllTasks)
    const lists = useAppSelector(selectAllLists)
    const projects = useAppSelector(selectAllProjects)
    const memberProjectIds = useAppSelector(selectMemberProjectIds)
    const tasksLoading = useAppSelector(selectTasksLoading)
    const listsLoading = useAppSelector(selectListsLoading)
    const projectsLoading = useAppSelector(selectProjectsLoading)

    const listIds = useMemo(() => new Set(lists.map((l: any) => l.id)), [lists])
    const memberProjectIdsSet = useMemo(() => new Set(memberProjectIds), [memberProjectIds])

    // Group tasks: projectId -> listId -> Task[]
    const groupTasksByProjectAndList = () => {
        const grouped: Record<string, Record<string, typeof tasks>> = {}
        tasks.forEach((task: any) => {
            if (!listIds.has(task.listId)) return
            if (!memberProjectIdsSet.has(task.projectId)) return

            if (!grouped[task.projectId]) {
                grouped[task.projectId] = {}
            }
            if (!grouped[task.projectId][task.listId]) {
                grouped[task.projectId][task.listId] = []
            }
            grouped[task.projectId][task.listId].push(task)
        })
        Object.values(grouped).forEach(listMap =>
            Object.keys(listMap).forEach(listId => {
                listMap[listId] = listMap[listId].slice().sort((a: any, b: any) => a.order - b.order)
            })
        )
        return grouped
    }

    const getList = (listId: string) => {
        return lists.find((l: any) => l.id === listId)
    }

    const getProject = (projectId: string) => {
        return projects.find((p: any) => p.id === projectId)
    }

    const handleTaskToggleComplete = (taskId: string, isCompleted: boolean) => {
        dispatch(toggleCompleteSaga({ taskId, isCompleted }))
    }

    const handleTaskAssign = (taskId: string, assigneeId: string | null) => {
        dispatch(updateTaskSaga({ taskId, data: { assigneeId } }))
    }

    if (loading || listsLoading || projectsLoading || tasksLoading) {
        return <PlateLoader fullscreen />
    }

    if (!workspace || workspace.workspaceUrlId !== workspaceUrlId) {
        return <Navigate to={"/"} />
    }

    const tasksByProject = groupTasksByProjectAndList()
    const projectEntries = Object.entries(tasksByProject).sort(([aId], [bId]) => {
        const aName = getProject(aId)?.name ?? ''
        const bName = getProject(bId)?.name ?? ''
        return aName.localeCompare(bName)
    })

    return (
        <div className={css.container}>
            {projectEntries.length === 0 ? (
                <Empty title="All tasks" text="There's nothing here." />
            ) : (
                projectEntries.map(([projectId, listMap]) => {
                    const project = getProject(projectId)
                    return (
                        <div className={css.section} key={projectId}>
                            <div className={css.sectionTitle}>
                                <TextH2><Link href={`/${workspaceUrlId}/projects/${project?.id}`}>{project?.name || 'Untitled'}</Link></TextH2>
                            </div>
                            <div className={css.sectionContent}>
                                <div className={css.lists}>
                                    {Object.entries(listMap).map(([listId, tasks]) => {
                                        const list = getList(listId)
                                        if (!list) return null
                                        return (
                                            <Section
                                                key={listId}
                                                title={list.name}
                                                tasks={tasks}
                                                onTaskToggleComplete={handleTaskToggleComplete}
                                                onTaskAssign={handleTaskAssign}
                                                showCompletedTasks={true}
                                            />
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )
                })
            )}
        </div>
    )
}

export default WorkspaceAllTasksPage
